/**
 * Which batch row is which, on a batch-picker table.
 *
 * Import-free on purpose, so it can be unit tested — the component that uses it
 * pulls in the medicine autocomplete, which pulls in axios's ESM build and
 * cannot be loaded under Jest.
 */

/**
 * Stable identity for one batch row.
 *
 * Deliberately NOT batch_number. An item that is not batch-tracked carries NULL
 * there on every row, but each GRN receipt still gets its own stock-balance row
 * — the ledger keys a balance on grn_item_id, not on the batch label. Two such
 * rows in one store used to collide: ticking one checkbox ticked both, a
 * quantity typed into one landed on both, and React saw duplicate keys.
 *
 * Now that each row can carry its OWN pack size, two rows melting into one is
 * worse than cosmetic, so identity comes from the balance row itself.
 *
 * Order matters: stock_balance_id is the ledger's own row id and is what the
 * store catalogue sends. The transaction-item ids are what older
 * prescribe/search shapes carry for the same row. grn_item_id identifies the
 * receipt behind it. batch_number is the last resort, and the only one that can
 * be ambiguous.
 */
export function batchRowKey(batch) {
  const id = batch?.stock_balance_id
    ?? batch?.inventory_transaction_item_id
    ?? batch?.transaction_item_id
    ?? batch?.grn_item_id
    ?? batch?.batch_number;
  return id == null || id === '' ? '' : String(id);
}

/**
 * Is this batch line the one belonging to this catalogue row?
 *
 * `rowKey` is batchRowKey(batch), passed in so a render pass resolves it once.
 */
export function isLineForRow(bl, rowKey, batch) {
  if (!bl || !batch) return false;
  // Lines built before rows carried a key — a restored draft, or a caller that
  // seeds batchLines itself — can only be matched the old way. A NULL label
  // identifies nothing, so such a line matches no row rather than matching
  // every unlabelled one: the row simply shows unticked and the user re-picks
  // it, which is recoverable. Silently editing the wrong row is not.
  if (bl._row_key == null || bl._row_key === '') {
    return bl.batch_number != null && bl.batch_number === batch.batch_number;
  }
  return bl._row_key === rowKey;
}
